import { useEffect, useRef, useState } from "react";
import { StepContainer } from "../components/StepContainer";
import { AudienceFields, AUDIENCE_MIN_CHARS } from "../components/fields/AudienceFields";
import { useUpdateBusinessContext } from "../hooks/useUpdateBusinessContext";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { STEPS, findStepIndexBySlug } from "../config/steps";

interface PersonaShape {
  summary?: string;
  [k: string]: unknown;
}

export interface AudienceStepProps {
  onValidityChange: (valid: boolean) => void;
  onAttemptContinue: (continueFn: () => Promise<boolean>) => void;
  onMissingChange?: (labels: string[]) => void;
}

export function AudienceStep({
  onValidityChange,
  onAttemptContinue,
  onMissingChange,
}: AudienceStepProps) {
  const step = STEPS[findStepIndexBySlug("publico")];
  const { mutate, mutateDebounced } = useUpdateBusinessContext();
  const { query } = useBusinessContext();

  const [summary, setSummary] = useState("");
  const summaryRef = useRef(summary);
  summaryRef.current = summary;

  useEffect(() => {
    const row = query.data;
    if (!row) return;
    const persona = (row.persona ?? {}) as PersonaShape;
    setSummary(typeof persona.summary === "string" ? persona.summary : "");
  }, [query.data]);

  useEffect(() => {
    const ok = summary.trim().length >= AUDIENCE_MIN_CHARS;
    onValidityChange(ok);
    if (onMissingChange) {
      onMissingChange(ok ? [] : [`resumo do cliente ideal (≥${AUDIENCE_MIN_CHARS} chars)`]);
    }
  }, [summary, onValidityChange, onMissingChange]);

  useEffect(() => {
    onAttemptContinue(async () => {
      const v = summaryRef.current.trim();
      if (v.length < AUDIENCE_MIN_CHARS) return false;
      const existing = ((query.data?.persona ?? {}) as PersonaShape) ?? {};
      await mutate({
        persona: { ...existing, summary: v },
        persona_generated_at: new Date().toISOString(),
        persona_generated_by: "user",
      } as never);
      return true;
    });
  }, [mutate, onAttemptContinue, query.data]);

  const handleChange = (v: string) => {
    setSummary(v);
    const existing = ((query.data?.persona ?? {}) as PersonaShape) ?? {};
    mutateDebounced({ persona: { ...existing, summary: v } } as never);
  };

  const handleGenerated = (text: string) => {
    const existing = ((query.data?.persona ?? {}) as PersonaShape) ?? {};
    void mutate({
      persona: { ...existing, summary: text },
      persona_generated_at: new Date().toISOString(),
      persona_generated_by: "ai",
    } as never);
  };

  return (
    <StepContainer
      icon={step.icon}
      headline={step.headline}
      sub={step.sub}
      importanceNote="Esse resumo orienta a IA a falar com o cliente certo, não com o público geral."
    >
      <AudienceFields
        value={summary}
        onChange={handleChange}
        onGenerated={handleGenerated}
      />
    </StepContainer>
  );
}
